/**
 * Route-level error boundary. React Router v7 exposes `errorElement`
 * per route, but we also want one at the AppShell layer so a crash
 * inside a feature doesn't blank the whole app.
 */

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);

    return (
      <Stack spacing={2} sx={{ p: 3, maxWidth: 720 }}>
        <Alert severity="error">
          <AlertTitle>
            <FormattedMessage id="error.unexpected" defaultMessage="Something went wrong" />
          </AlertTitle>
          <code style={{ whiteSpace: 'pre-wrap' }}>{this.state.error.message}</code>
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={this.reset}>
            <FormattedMessage id="error.retry" defaultMessage="Retry" />
          </Button>
          <Button variant="text" onClick={() => window.location.reload()}>
            <FormattedMessage id="error.reload" defaultMessage="Reload" />
          </Button>
        </Stack>
      </Stack>
    );
  }
}
